import PhonePreviewBannerImage from '@/assets/images/phone-preview-banner.svg';
import { BoxInfo, ButtonDialog, FileUpload } from '@/components';
import { CountCharMessage } from '@/components/countCharMessage/countCharMessage';
import TextFieldValidate from '@/components/textFieldValidate';
import GridGroup from '@/components/webForm/gridGroup';
import Header from '@/core/components/header/header';
import { BannerTextPreview } from '@/modules/customization/components/bannerPreview/text/bannerTextPreview';
import { CardMessage } from '@/modules/customization/components/cardMessage/cardMessage';
import { CropImageModal } from '@/modules/customization/components/cropImageModal/cropImageModal';
import { PhonePreview } from '@/modules/customization/components/phonePreview/phonePreview';
import { FormAppCustomizableItems } from '@/modules/customization/entities/interfaces/customizations.interfaces';
import { LIMIT_CHARACTERS_FILE_NAME } from '@/modules/files/constants/files';
import ICONS from '@/shared/entities/constants/icons';
import { FormFile } from '@/shared/entities/types/formFile.type';
import ObjectType from '@/shared/entities/types/object.type';
import removeEmptyParams from '@/utils/removeEmptyParams';
import { isEmpty, pick } from 'lodash';
import React, { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { Text, Toggle } from 'vkit/lib/components';
import { Grid } from 'vkit/lib/context';

export interface Banner {
  file: FormFile | null;
  description: string;
  buttonLabel?: string;
  buttonLink?: string;
  initialDate?: string;
  endDate?: string;
}

interface BannerCustomizationProps {
  savedFileUrl?: string | null;
  formAppCustomizationDefault: Partial<FormAppCustomizableItems>;
  formAppCustomization: FormAppCustomizableItems;
  onChange: (fieldKey: keyof FormAppCustomizableItems, fieldValue: unknown) => void;
  onBlur: (fieldKey: keyof FormAppCustomizableItems, fieldValue: unknown) => void;
  errors?: { [key in keyof FormAppCustomizableItems]?: string };
  shouldHasLink?: boolean;
  setShouldHasLink?: Dispatch<SetStateAction<boolean>>;
  loading?: boolean;
  onRemoveFile?: () => void;
  onCleanFields?: () => void;
}

export const BannerCustomization: React.FC<BannerCustomizationProps> = ({
  savedFileUrl,
  formAppCustomizationDefault,
  formAppCustomization,
  onChange,
  onBlur,
  errors,
  shouldHasLink,
  setShouldHasLink,
  loading,
  onRemoveFile,
  onCleanFields,
}) => {
  const [openCropImageModal, setOpenCropImageModal] = useState(false);
  const bannerDescriptionMaxLength = 60;
  const bannerButtonLabelMaxLength = 10;

  const fileUrl = useMemo(
    () =>
      formAppCustomization.bannerFile
        ? URL.createObjectURL(formAppCustomization.bannerFile)
        : savedFileUrl,
    [formAppCustomization.bannerFile, savedFileUrl],
  );

  const showCleanFieldsButton = useMemo(() => {
    const fields = pick(formAppCustomization, [
      'bannerDescription',
      'bannerInitialDate',
      'bannerEndDate',
      'buttonLabel',
      'buttonLink',
    ]);

    return !isEmpty(removeEmptyParams(fields));
  }, [formAppCustomization]);

  return (
    <>
      <CropImageModal
        open={openCropImageModal}
        setOpen={setOpenCropImageModal}
        setFile={(file) => onChange('bannerFile', file)}
        file={formAppCustomization?.bannerFile || null}
        aspectRatio={4 / 3}
        mimeType='image/jpeg'
        quality={0.5}
      />
      <Grid column gap={16}>
        <Header
          title='Banner'
          description='Personalize uma imagem de banner para aparecer na tela inicial do aplicativo.'
          icon={ICONS.IMAGES}>
          <Grid margin='18px 0 0'>
            <PhonePreview label='Onde o banner deve aparecer?'>
              <img
                src={PhonePreviewBannerImage}
                alt='Pré visualização do aplicativo com banner'
                style={{ width: 'calc(100% + 12px)', margin: '-6px -6px -10px' }}
              />
            </PhonePreview>
          </Grid>
        </Header>

        <BoxInfo
          loading={loading}
          footerAlignContent='left'
          footer={
            fileUrl && (
              <ButtonDialog
                solo
                color='danger'
                text='Tem certeza que deseja excluir a imagem?'
                onClick={() => {
                  onChange('bannerFile', null);
                  onRemoveFile?.();
                }}
                label='Excluir imagem'
                iconColor='danger'
                icon={ICONS.TRASH}
                dialogTextIcon={ICONS.TRASH}
                noDialogHeader
                buttonYesLabel='Excluir'
                buttonNoLabel='Cancelar'
              />
            )
          }>
          <Grid gap={16} stretch>
            <Grid>
              <div
                style={{
                  width: 215,
                  marginRight: -3,
                }}>
                {fileUrl ? (
                  <img
                    src={fileUrl}
                    alt='Banner'
                    style={{ height: 160, borderRadius: '7px 0 0 7px' }}
                  />
                ) : (
                  <FileUpload
                    name='file'
                    title='Arraste uma imagem aqui'
                    titleSize='medium'
                    description='ou selecione um arquivo<br />do seu computador'
                    descriptionSize='medium'
                    icon={null}
                    onChange={(_name: string, value: File) => {
                      onChange('bannerFile', value);
                      setOpenCropImageModal(true);
                    }}
                    onDelete={() => {
                      onChange('bannerFile', null);
                    }}
                    allowedExtensions={['png', 'jpg', 'jpeg']}
                    maxCharacterLimit={LIMIT_CHARACTERS_FILE_NAME}
                    required
                    formValidatorErrors={{ file: errors?.bannerFile }}
                    style={{
                      fileUpload: {
                        borderRadius: '7px 0 0 7px',
                        borderWidth: '1px 0 1px 1px',
                        height: 160,
                      },
                    }}
                  />
                )}
              </div>

              <BannerTextPreview
                width={277}
                height={160}
                description={
                  fileUrl
                    ? formAppCustomization.bannerDescription ||
                      formAppCustomizationDefault.bannerDescription
                    : formAppCustomizationDefault.bannerDescription
                }
                buttonLabel={
                  shouldHasLink
                    ? formAppCustomization.buttonLabel || formAppCustomizationDefault.buttonLabel
                    : ''
                }
                buttonLink={formAppCustomization.buttonLink}
              />
            </Grid>

            <Grid column gap={16}>
              <CardMessage
                height={72}
                width={270}
                padding={8}
                icon={ICONS.CROP}
                color='warning'
                message='<b>Formato:</b> PNG ou JPEG<br /><b>Resolução miníma:</b> 400x300 pixels'
              />

              <CardMessage
                height={72}
                width={270}
                padding={8}
                icon={ICONS.BULB}
                color='success'
                message='Recomendamos o uso de uma imagem com proporção aproximada de 4:3'
              />
            </Grid>
          </Grid>
        </BoxInfo>

        <BoxInfo
          title='Conteúdo'
          margin={0}
          loading={loading}
          header={
            showCleanFieldsButton && (
              <ButtonDialog
                solo
                color='danger'
                text='Tem certeza que deseja limpar todos os campos?'
                onClick={onCleanFields}
                label='Limpar campos'
                iconColor='danger'
                icon={ICONS.TRASH}
                dialogTextIcon={ICONS.TRASH}
                noDialogHeader
                buttonYesLabel='Limpar campos'
                buttonNoLabel='Cancelar'
              />
            )
          }>
          <GridGroup
            gap={32}
            body={[
              {
                default: 100,
                middle: 100,
                component: (
                  <TextFieldValidate
                    label='Texto do banner'
                    name='bannerDescription'
                    placeholder={formAppCustomizationDefault.bannerDescription}
                    useValues={formAppCustomization as ObjectType}
                    onChange={onChange}
                    onBlur={onBlur}
                    maxLength={bannerDescriptionMaxLength}
                    useErrors={errors}
                    header={
                      <CountCharMessage
                        value={formAppCustomization.bannerDescription}
                        limit={bannerDescriptionMaxLength}
                      />
                    }
                  />
                ),
              },
              {
                default: 20,
                middle: 100,
                component: (
                  <Grid column gap={20} stretch>
                    <Text value='Link no banner' title />
                    <Grid gap={8} alignItems='center'>
                      <Text value={shouldHasLink ? 'Habilitado' : 'Desabilitado'} size='larger' />
                      <Toggle
                        checked={Boolean(shouldHasLink)}
                        handleChange={() => {
                          setShouldHasLink?.(!shouldHasLink);
                        }}
                      />
                    </Grid>
                  </Grid>
                ),
              },
              {
                default: 40,
                middle: 100,
                component: (
                  <TextFieldValidate
                    label='Adicionar link'
                    name='buttonLink'
                    disabled={!shouldHasLink}
                    required={Boolean(fileUrl) && shouldHasLink}
                    useValues={formAppCustomization as ObjectType}
                    onChange={onChange}
                    onBlur={onBlur}
                    icon={ICONS.LINK_CHAIN}
                    iconDir='left'
                    useErrors={errors}
                  />
                ),
              },
              {
                default: 40,
                middle: 100,
                component: (
                  <TextFieldValidate
                    label='Texto do botão'
                    name='buttonLabel'
                    disabled={!shouldHasLink}
                    placeholder={formAppCustomizationDefault.buttonLabel || ''}
                    useValues={formAppCustomization as ObjectType}
                    onChange={onChange}
                    onBlur={onBlur}
                    useErrors={errors}
                    maxLength={bannerButtonLabelMaxLength}
                    header={
                      <CountCharMessage
                        value={formAppCustomization.buttonLabel}
                        limit={bannerButtonLabelMaxLength}
                      />
                    }
                  />
                ),
              },
              {
                default: 50,
                middle: 100,
                component: (
                  <TextFieldValidate
                    label='Início da exibição'
                    name='bannerInitialDate'
                    type='date'
                    required={Boolean(fileUrl)}
                    useValues={formAppCustomization as ObjectType}
                    onChange={onChange}
                    onBlur={onBlur}
                    useErrors={errors}
                  />
                ),
              },
              {
                default: 50,
                middle: 100,
                component: (
                  <TextFieldValidate
                    label='Fim da exibição (Opcional)'
                    name='bannerEndDate'
                    type='date'
                    useValues={formAppCustomization as ObjectType}
                    onChange={onChange}
                    onBlur={onBlur}
                    useErrors={errors}
                  />
                ),
              },
            ]}
          />
        </BoxInfo>
      </Grid>
    </>
  );
};
